/**
 * Pantalla principal: organizaciones del usuario y, dentro de cada una, sus módulos
 * (docs/FASE-7.md §7). Todo sale de `GET /me/orgs`, que ya existía desde la fase 0.
 *
 * Aquí es donde se cumple el criterio de salida de la fase: cada módulo es un enlace a su `baseUrl`
 * con `cnt_org=<slug>` (§8). El módulo de destino lo lee, lo pasa como `org_hint`, y con la sesión
 * de dominio viva entra sin ver una pantalla de acceso.
 */
import { withOrgParam } from "@continnua/auth-web";
import type { MeOrgsEntry, MeOrgsModule, SubscriptionStatus } from "@continnua/contracts";
import type { ReactElement } from "react";

/**
 * Por qué un módulo puede estar y no poder abrirse. `past_due` y `suspended` vienen de la fase 6:
 * entrar ahí solo produciría un `402` del propio módulo, así que se pinta desactivado y con el
 * motivo, en vez de ofrecer un enlace que falla.
 */
const MOTIVO_NO_DISPONIBLE: Partial<Record<SubscriptionStatus, string>> = {
  past_due: "Hay una factura vencida sin pagar.",
  suspended: "La suscripción está suspendida.",
  cancelled: "La suscripción está cancelada.",
};

function disponible(module: MeOrgsModule): boolean {
  return MOTIVO_NO_DISPONIBLE[module.subscriptionStatus] === undefined;
}

function TarjetaOrganizacion({ entrada }: { entrada: MeOrgsEntry }): ReactElement {
  const { organization, modules, isDefault } = entrada;

  return (
    <section className="org-card">
      <header>
        <h2>{organization.displayName}</h2>
        {isDefault && <span className="muted">predeterminada</span>}
      </header>

      {modules.length === 0 ? (
        // No es un error ni una pantalla vacía por accidente: `/me/orgs` filtra los módulos donde el
        // usuario no tiene ningún rol (docs/FASE-7.md §3.3), y conviene decirlo.
        <p className="muted">
          No tienes ningún rol asignado en los módulos de esta organización. Pídeselo a quien la administre.
        </p>
      ) : (
        <div className="module-grid">
          {modules.map((module) =>
            disponible(module) ? (
              <a key={module.code} className="module-link" href={withOrgParam(module.baseUrl, organization.slug)}>
                {module.name}
                <span className="module-role">{module.roles.join(", ")}</span>
              </a>
            ) : (
              <span
                key={module.code}
                className="module-link-disabled"
                aria-disabled="true"
                title={MOTIVO_NO_DISPONIBLE[module.subscriptionStatus]}
              >
                {module.name}
                <span className="module-role">{MOTIVO_NO_DISPONIBLE[module.subscriptionStatus]}</span>
              </span>
            ),
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Las organizaciones llegan por props: las carga `App` con `useOrganizaciones()` para no pedir la
 * misma lista una vez por pantalla (ver `../organizaciones.ts`).
 */
export function Inicio({ orgs, error }: { orgs: MeOrgsEntry[] | null; error: string | null }): ReactElement {
  if (error !== null) return <p className="error">{error}</p>;
  if (orgs === null) return <p className="muted">Cargando tus organizaciones…</p>;

  if (orgs.length === 0) {
    return (
      <div className="aviso">
        Todavía no perteneces a ninguna organización. Cuando alguien te invite, aparecerá aquí.
      </div>
    );
  }

  return (
    <>
      {orgs.map((entrada) => (
        <TarjetaOrganizacion key={entrada.organization.id} entrada={entrada} />
      ))}
    </>
  );
}
